import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import usePageTitle from "@/hooks/usePageTitle";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CancelRegistrationPage() {
  const { token } = useParams();
  usePageTitle("Annulation");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/cancel/${token}`);
        setData(res.data);
        if (res.data.already_cancelled) setCancelled(true);
      } catch (e) {
        setError(e.response?.data?.detail || "Lien invalide");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleCancel = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API}/api/public/cancel/${token}`);
      setCancelled(true);
      toast.success("Participation annulee");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
        <div className="animate-pulse text-[#64748b]">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center p-4">
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-8 max-w-md w-full text-center">
          <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-white text-xl font-bold mb-2">Lien invalide</h1>
          <p className="text-[#94A3B8] text-sm">{error}</p>
          <Link to="/evenements" className="inline-block mt-6 text-[#0099CC] hover:underline text-sm">Retour aux evenements</Link>
        </div>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center p-4" data-testid="cancel-confirmed">
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-8 max-w-md w-full text-center">
          <CheckCircle2 className="h-12 w-12 text-[#10B981] mx-auto mb-4" />
          <h1 className="text-white text-xl font-bold mb-2">Annulation confirmee</h1>
          <p className="text-[#94A3B8] text-sm mb-1">
            {data?.participant?.first_name} {data?.participant?.last_name}
          </p>
          <p className="text-[#64748b] text-sm">Votre participation a ete annulee. Votre QR code d'acces a ete invalide.</p>
          {data?.event && <p className="text-[#0099CC] text-sm mt-4">{data.event.title}</p>}
          <Link to="/evenements" className="inline-block mt-6 text-[#0099CC] hover:underline text-sm">Voir les evenements</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center p-4" data-testid="cancel-page">
      <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-8 max-w-md w-full text-center">
        <AlertTriangle className="h-12 w-12 text-[#F59E0B] mx-auto mb-4" />
        <h1 className="text-white text-xl font-bold mb-2">Annuler votre participation ?</h1>
        {data?.event && (
          <p className="text-[#0099CC] font-semibold text-lg mb-2">{data.event.title}</p>
        )}
        <p className="text-[#94A3B8] text-sm mb-1">
          {data?.participant?.first_name} {data?.participant?.last_name}
        </p>
        {data?.participant?.reference && (
          <p className="text-[#64748b] text-xs font-mono mb-4">Ref: {data.participant.reference}</p>
        )}
        <p className="text-[#94A3B8] text-sm mb-6">
          En confirmant, votre QR code sera invalide et votre place sera liberee.
        </p>
        <div className="flex gap-3 justify-center">
          <Link to="/evenements">
            <Button variant="outline" className="border-[#2A3441] text-[#94A3B8]">
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Retour
            </Button>
          </Link>
          <Button
            data-testid="confirm-cancel-btn"
            onClick={handleCancel}
            disabled={confirming}
            className="bg-red-500 hover:bg-red-600 text-white font-bold"
          >
            {confirming ? "Annulation..." : "Confirmer l'annulation"}
          </Button>
        </div>
      </div>
    </div>
  );
}
